import { formatDay, parseBackendDate } from "@/datox/v2/dates"
import { workspaceActiveRun, workspaceRunStatus, type FactsheetWorkspace } from "@/datox/workspaces"
import { lastRunFailed } from "@/datox/v2/workflow-step-status"

// One status and one data date for a report, shared by the editor's top bar and the reports list (UX research P2, P3,
// E24, W3). Display rules only: the backend's readiness stays the source, these decide what the banker reads.

type DashboardLike = { dashboard_metadata?: Record<string, unknown> | null } | null | undefined

// The date the figures run to. Only a finished factsheet run stamps a real one: `dashboard_metadata.as_of`
// ("2026-04-29") on a dashboard whose `factsheet_run_id` is not a draft. Drafts and empty reports carry the upload or
// request day there, which is not a data date.
export function dataDate(dashboard: DashboardLike): Date | undefined {
  if (!filledByRun(dashboard)) return undefined
  const value = dashboard!.dashboard_metadata!.as_of
  return typeof value === "string" ? parseBackendDate(value) : undefined
}

// A finished factsheet run filled this dashboard (its `factsheet_run_id`, not a draft), whether or not it stamped a data
// date: `as_of` can be null (code review), and a sheet with data is still filled.
export function filledByRun(dashboard: DashboardLike) {
  const run = dashboard?.dashboard_metadata?.factsheet_run_id
  return typeof run === "string" && !!run && !run.startsWith("draft:")
}

// "29 Apr 2026" (dates.ts formatDay: one format app-wide, read in UTC so the day never drifts).
export function formatDataDate(date: Date) {
  return formatDay(date)
}

// The one date line of a report:
//   1. "Data to <date>": the dashboard's real data date (above), else the workspace's config.data_end (if the backend
//      ever sends it), else the client's factsheet "(source YYYY-MM-DD)" date, the one Workflow shows;
//   2. else "Uploaded <date>" (config.as_of, the upload day);
//   3. nothing when the report has no files: an upload date next to "No data yet" contradicts itself (E24).
export type DataLine = { kind: "dataTo" | "uploaded"; date: Date }
export function reportDataLine(input: {
  dashboard?: DashboardLike
  dataEnd?: string | null
  sourceTitle?: string | null
  uploaded?: string | null
  fileCount: number
}): DataLine | undefined {
  if (input.fileCount <= 0) return undefined
  const end = dataDate(input.dashboard) ?? parseBackendDate(input.dataEnd) ?? parseBackendDate(sourceDateOf(input.sourceTitle))
  if (end) return { kind: "dataTo", date: end }
  const uploaded = parseBackendDate(input.uploaded)
  return uploaded ? { kind: "uploaded", date: uploaded } : undefined
}

// "Hamad_70714 factsheet (source 2026-04-29)" → "2026-04-29".
export function sourceDateOf(title?: string | null) {
  return title?.match(/source (\d{4}-\d{2}-\d{2})/)?.[1]
}

// Whether the report's sheet has something to show, from the workspace alone (the list has nothing else): a period,
// files, and a completed run or a produced report. The editor passes what it knows better (its loaded tables).
export function workspaceHasData(item: FactsheetWorkspace | undefined) {
  if (!item?.period_id || !item.config.file_ids.length) return false
  return item.reports.length > 0 || item.runs.some((run) => run.status === "completed")
}

export type ReportState = "running" | "failed" | "needsData" | "stale" | "ready"
export type ReportStatus = {
  state: ReportState
  // An i18n key (existing copy): Ready, Needs data, Out of date, Running, or the run's own phase.
  label: "datox.report.ready" | "datox.report.needsData" | "datox.studio.staleLabel" | "datox.report.running"
    | "datox.workflow.processing.interrupted" | "datox.workflow.processing.waitingForFiles" | "datox.workflow.processing.cancelling"
    | "datox.v2.report.status.failed"
  tone: "approved" | "warning" | "bad" | "none"
  // Safe to send: ready with data and nothing running.
  sendable: boolean
}

// One status rule (P3). A run in progress wins; then a failed last run (§74j: Artyom, on "Ready" after a failed run,
// «почему статус готово тогда?»), sendable only on what an earlier run completed; then the backend's stale flag; then
// "Needs data" only when the sheet really has nothing to show. The backend says needs_data on full reports when a category is missing (Hamad has no
// categories but has every table): those read "Ready", neutral, with no "Create a category" hint for the banker.
export function reportStatus(item: FactsheetWorkspace | undefined, options: { hasData?: boolean } = {}): ReportStatus {
  const hasData = options.hasData ?? workspaceHasData(item)
  const run = workspaceRunStatus(item)
  if (run === "interrupted") return { state: "running", label: "datox.workflow.processing.interrupted", tone: "warning", sendable: false }
  if (run === "waiting_for_files") return { state: "running", label: "datox.workflow.processing.waitingForFiles", tone: "warning", sendable: false }
  if (run === "cancel_requested") return { state: "running", label: "datox.workflow.processing.cancelling", tone: "none", sendable: false }
  if (workspaceActiveRun(item)) return { state: "running", label: "datox.report.running", tone: "none", sendable: false }
  if (lastRunFailed(item?.runs ?? [])) {
    // Sendable when the data on the sheet is an earlier good one, not what the failed run left half-done: a completed
    // run in the history, or (the history cut short, or the data made elsewhere) a factsheet or a sent export from
    // before (code review: Send was blocked over good earlier data).
    const earlier = item!.runs.some((entry) => !entry.check_only && entry.status === "completed") || item!.reports.length > 0 || (item!.exports ?? []).some((entry) => !entry.draft)
    return { state: "failed", label: "datox.v2.report.status.failed", tone: "bad", sendable: hasData && earlier }
  }
  if (item?.stale || item?.readiness === "stale") return { state: "stale", label: "datox.studio.staleLabel", tone: "warning", sendable: hasData }
  if (item?.readiness === "ready") return { state: "ready", label: "datox.report.ready", tone: "approved", sendable: true }
  if (hasData) return { state: "ready", label: "datox.report.ready", tone: "none", sendable: true }
  return { state: "needsData", label: "datox.report.needsData", tone: "warning", sendable: false }
}
